import { Box } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { PolicyOverview, Product } from '@acorn-portal/shared/src/services/api';
import { getPolicyRoute } from '@acorn-portal/shared/src/utils/policy-routes';
import { IPaging } from '@acorn-portal/shared/src/models/policy-model';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import PolicyListItem from './PolicyListItem';
import PolicyListPagination from './PolicyListPagination';
import useClearStore from '@acorn-portal/shared/src/hooks/useClearStore';
import useGetRenewalDetails from '../../hooks/useGetRenewalDetails';

interface IPolicyListProps {
	policies: PolicyOverview[] | undefined;
	paging: IPaging;
	isPaging: boolean;
}

const PolicyList: FC<IPolicyListProps> = ({ policies, paging, isPaging }) => {
	const { clearStore } = useClearStore();
	const navigate = useNavigate();
	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		paging.setPageNumber(value);
	};
	const { getPolicyAndRateRenewal } = useGetRenewalDetails();
	const [loadingRenewal, setLoadingRenewal] = useState(false);
	const loading = isPaging || loadingRenewal;

	useEffect(() => {
		clearStore();
	}, []);

	const handleSelect = (policyToken: string, policyNumber: string, product: Product) => {
		navigate(getPolicyRoute(product, policyToken, policyNumber));
	};

	const handleRenew = async (policyToken: string, policyNumber: string) => {
		setLoadingRenewal(true);
		getPolicyAndRateRenewal(policyToken, policyNumber, () => {
			navigate(ROUTE_PATHS.renewalMakeAChangeSummary);
		});
	};

	const handleSubscription = (policyToken: string, policyNumber: string) => {
		const encodedPolicyNumber = encodeURIComponent(policyNumber);
		navigate(`${ROUTE_PATHS.subscriptionRestart}/${policyToken}?policyNumber=${encodedPolicyNumber}`);
	};

	return (
		<Box
			sx={(theme) => ({
				width: '70%',
				margin: '0 auto',
				paddingBottom: '2rem',
				[theme.breakpoints.down('md')]: {
					width: '100%',
				},
			})}
		>
			<LoadingModal open={loading} message={loading ? 'Loading...' : ''} />
			<Box>
				{policies?.map((x) => (
					<PolicyListItem
						key={x.policyNumber}
						policy={x}
						onSelect={handleSelect}
						onRenew={handleRenew}
						onSubscription={handleSubscription}
					/>
				))}
			</Box>
			<Box>
				<PolicyListPagination
					numberOfListItems={paging.totalItemCount}
					page={paging.pageNumber}
					pageSize={paging.pageSize}
					onChange={handlePageChange}
				/>
			</Box>
		</Box>
	);
};

export default PolicyList;
